/** @format */
import { Link, useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useContext, useEffect, useState } from "react";
import moment from "jalali-moment";

import Master from "../Layouts/Master";
import { EventContext } from "../../Contexts/EventContext";
import { error } from "../../Shared/Components/Toast";
import useApi from "../../Shared/hooks/api/Index";
import Loading from "../../Shared/Components/Loading";
import {
  htmlToJsx,
  userFitsMatchGender,
  matchGenderLabel,
  resolveMatchRegistrationKind,
  submitGatewayForm,
  number_format,
} from "../../Shared/utils/helper";
import { checkLogin } from "../../Shared/hooks/authenticate";
import { success } from "../../Shared/Components/Toast";

const AGE_INELIGIBLE_MESSAGE =
  "امکان ثبت‌نام در این مسابقه وجود ندارد؛ سن شما در محدوده رده‌های سنی تعریف‌شده قرار نمی‌گیرد. در صورت نیاز با پشتیبانی فدراسیون تماس بگیرید.";

const GENDER_INELIGIBLE_MESSAGE =
  "امکان ثبت‌نام در این مسابقه وجود ندارد؛ جنسیت شما با محدودیت جنسیتی این مسابقه مطابقت ندارد.";

const RESERVE_INELIGIBLE_MESSAGE =
  "ثبت‌نام این مسابقه فقط برای افراد لیست رزرو امکان‌پذیر است.";

const birthdayFitsAge = (birthday, startAge, endAge) => {
  if (!birthday || !startAge || !endAge) return false;
  return startAge <= birthday && endAge >= birthday;
};

const findEligibleMatchAge = (matchAges, birthday, kind = 'single') => {
  const list = matchAges?.[kind] || [];
  return (
    list.find((age) =>
      birthdayFitsAge(birthday, age.start_age, age.end_age)
    ) || null
  );
};

const Event = () => {
  const { slug } = useParams();

  const eventContext = useContext(EventContext);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [matchAgeId, setMatchAgeId] = useState(null);
  const [eligiblePersonPrice, setEligiblePersonPrice] = useState(null);
  const [genderEligible, setGenderEligible] = useState(true);
  const [ageCheckDone, setAgeCheckDone] = useState(false);
  const [userLoading, setUserLoading] = useState(false);
  const [formSubmitLoading, setFormSubmitLoading] = useState(false);
  const [walletLoading, setWalletLoading] = useState(false);
  const [approvedClubs, setApprovedClubs] = useState([]);
  const [selectedClubId, setSelectedClubId] = useState("");
  const [registrationKind, setRegistrationKind] = useState("single");
  const [matchKindId, setMatchKindId] = useState(1);
  const [reserveEligible, setReserveEligible] = useState(true);

  const submitGatewayPayment = (gatewayData) => {
    if (!submitGatewayForm(gatewayData)) {
      error("آدرس درگاه پرداخت دریافت نشد");
      return;
    }
    success("در حال هدایت به صفحه پرداخت لطفا منتظر بمانید");
  };

  const handleRegisterResponse = (data) => {
    if (data?.status === 406) {
      error(data?.message);
      setTimeout(() => navigate("/panel/profile"), 2000);
      return;
    }
    if (data?.status === 405) {
      error(data?.message);
      const sportId = data?.sport_id;
      if (sportId) {
        const returnPath = `/match/${slug}`;
        setTimeout(() => {
          navigate(
            `/panel/membership/payment?sport_id=${sportId}&return=${encodeURIComponent(returnPath)}`
          );
        }, 1500);
      }
      return;
    }
    if (data?.status === 201) {
      success(data?.message || "ثبت‌نام با موفقیت انجام شد");
      navigate(data?.redirect_to || "/panel/profile/match-registered");
      return;
    }
    if (data?.status === 200 && data?.data) {
      submitGatewayPayment(data.data);
      return;
    }
    if (data?.status === 422 || data?.status === 400 || data?.status === 403) {
      error(data?.message);
      return;
    }
    error(data?.message || "خطا در ثبت‌نام");
  };

  const registerToMatch = async (useWallet = false) => {
    if (!matchAgeId) {
      return error(AGE_INELIGIBLE_MESSAGE);
    }
    if (useWallet) {
      setWalletLoading(true);
    } else {
      setFormSubmitLoading(true);
    }
    const payload = {
      match: eventContext.match?.id,
      match_age: matchAgeId,
      match_kind_id: matchKindId,
    };
    if (registrationKind === 'league') {
      if (approvedClubs.length > 1 && !selectedClubId) {
        setFormSubmitLoading(false);
        setWalletLoading(false);
        return error('لطفاً باشگاه را انتخاب کنید');
      }
      if (selectedClubId) {
        payload.club_id = Number(selectedClubId);
      } else if (approvedClubs.length === 1) {
        payload.club_id = approvedClubs[0].id;
      }
    }
    if (useWallet) {
      payload.wallet = 1;
    }
    await useApi.post(
      "/match/register",
      payload,
      (res) => {
        handleRegisterResponse(res.data);
        setFormSubmitLoading(false);
        setWalletLoading(false);
      },
      (err) => {
        console.log(err);
        setFormSubmitLoading(false);
        setWalletLoading(false);
        error("خطا در ثبت‌نام");
      }
    );
  };
  const resolveUserAge = async (match) => {
    if (!checkLogin()) {
      setAgeCheckDone(false);
      setMatchAgeId(null);
      setEligiblePersonPrice(null);
      setGenderEligible(true);
      setReserveEligible(true);
      return;
    }

    setUserLoading(true);
    setAgeCheckDone(false);
    await useApi.get(
      "/user",
      {},
      async (res) => {
        const user = res.data.data;
        const birthday = user?.birthday;
        const kinds = match?.match_kinds || [];
        const { key: kindKey, id: kindId } = resolveMatchRegistrationKind(kinds);
        setRegistrationKind(kindKey);
        setMatchKindId(kindId);
        const isReserveList = match?.registration_mode === 'reserve_list';
        setReserveEligible(
          !isReserveList || match?.is_reserve_eligible === true
        );

        const eligible = findEligibleMatchAge(match?.match_ages, birthday, kindKey);
        if (eligible) {
          setMatchAgeId(eligible.match_id);
          setEligiblePersonPrice(
            eligible.person_price != null && eligible.person_price !== ''
              ? eligible.person_price
              : null
          );
        } else {
          setMatchAgeId(null);
          setEligiblePersonPrice(null);
        }

        if (kindKey === 'league') {
          await useApi.get(
            '/panel/club/my/approved-for-registration',
            {},
            (clubRes) => {
              const clubs = clubRes.data?.data ?? [];
              setApprovedClubs(clubs);
              if (clubs.length === 1) {
                setSelectedClubId(String(clubs[0].id));
              } else {
                setSelectedClubId('');
              }
            },
            () => {
              setApprovedClubs([]);
              setSelectedClubId('');
            }
          );
        } else {
          setApprovedClubs([]);
          setSelectedClubId('');
        }
        setGenderEligible(
          userFitsMatchGender(match?.gender, user?.gender)
        );
        setAgeCheckDone(true);
        setUserLoading(false);
      },
      () => {
        setMatchAgeId(null);
        setEligiblePersonPrice(null);
        setGenderEligible(true);
        setAgeCheckDone(true);
        setUserLoading(false);
      }
    );
  };

  useEffect(() => {
    const getInfo = async () => {
      setLoading(true);
      setAgeCheckDone(false);
      setMatchAgeId(null);
      setEligiblePersonPrice(null);
      setGenderEligible(true);
      setReserveEligible(true);
      await useApi.get(
        `/match/${slug}`,
        {},
        async (res) => {
          const match = res.data.data;
          eventContext.match = match;
          await resolveUserAge(match);
          setLoading(false);
        },
        () => {
          error("هنگام دریافت اطلاعات خطایی رخ داده است");
          setLoading(false);
        }
      );
    };
    getInfo();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  const isRegistrationOpen =
    moment()
      .locale("fa")
      .isBefore(
        moment(
          eventContext.match?.register_end ?? "1999-01-01 00:00:00",
          "YYYY-MM-DD HH:mm:ss"
        )
      ) &&
    moment()
      .locale("fa")
      .isAfter(
        moment(
          eventContext.match?.register_start ?? "1999-01-01 00:00:00",
          "YYYY-MM-DD HH:mm:ss"
        )
      );

  const matchPriceLabel = (() => {
    if (eligiblePersonPrice != null) {
      return number_format(eligiblePersonPrice);
    }
    if (eventContext.match?.price) {
      return eventContext.match.price;
    }
    const ages =
      eventContext.match?.match_ages?.[registrationKind] ||
      eventContext.match?.match_ages?.single ||
      [];
    const firstPrice = ages.find(
      (age) => age?.person_price != null && age?.person_price !== ''
    )?.person_price;
    return firstPrice != null ? number_format(firstPrice) : null;
  })();

  return loading ? (
    <Loading />
  ) : (
    <Master
      showSidebar={false}
      containerFullWidth={true}
      headerFixed={true}
      title={`ثبت نام | ${eventContext.match?.title} `}
    >
      <div className="image-cover">
        <div className="home-back-img w-100 d-flex flex-column">
          <div className="row mx-2 my-4">
            <div className="col-12 overflow-hidden mx-0 px-0">
              <div className="card card-hover border rounded overflow-hidden position-relative">
                <div className="bg-purple w-100 p-4 d-flex flex-row justify-content-center">
                  <span className="text-dark-mode f-16 font-weight-bold">
                    {eventContext.match.title}
                  </span>
                </div>
                <div className="card-body p-0 d-flex flex-column justify-content-center w-100 align-items-center">
                  <div className="d-flex flex-column p-2 justify-content-center w-100 align-items-start">
                    <span className="f-14 my-2">
                      شروع ثبت نام:{" "}
                      {moment(
                        eventContext.match?.register_start ??
                          "1990-01-01 00:00:00",
                        "YYYY-MM-DD HH:mm:ss"
                      )
                        .locale("fa")
                        .format("YYYY/MM/DD")}
                    </span>
                    <span className="f-14 my-2">
                      پایان ثبت نام :{" "}
                      {moment(
                        eventContext.match?.register_end ??
                          "1990-01-01 00:00:00",
                        "YYYY-MM-DD HH:mm:ss"
                      )
                        .locale("fa")
                        .format("YYYY/MM/DD")}
                    </span>
                    <span className="f-14 my-2">
                      شروع رویداد:{" "}
                      {moment(
                        eventContext.match?.start ?? "1990-01-01 00:00:00",
                        "YYYY-MM-DD HH:mm:ss"
                      )
                        .locale("fa")
                        .format("YYYY/MM/DD")}
                    </span>
                    <span className="f-14 my-2">
                      پایان رویداد :{" "}
                      {moment(
                        eventContext.match?.end ?? "1990-01-01 00:00:00",
                        "YYYY-MM-DD HH:mm:ss"
                      )
                        .locale("fa")
                        .format("YYYY/MM/DD")}
                    </span>

                    <span className="f-14 my-2">
                      استان محل برگزاری : {eventContext.match?.province?.name}
                    </span>

                    <span className="f-14 my-2">
                      شهر محل برگزاری : {eventContext.match?.city?.name}
                    </span>

                    <span className="f-14 my-2">
                      جنسیت : {matchGenderLabel(eventContext.match?.gender)}
                    </span>

                    {matchPriceLabel && (
                      <span className="f-14 my-2">
                        مبلغ ورودی:
                        <span className="d-inline-block px-1">
                          {matchPriceLabel}
                        </span>
                        ریال
                      </span>
                    )}

                    <div className="row w-100 overflow-auto">
                      <div className="col-12">
                        {htmlToJsx(eventContext.match?.description ?? "")}
                      </div>
                    </div>
                    {checkLogin() ? (
                      <>
                        {isRegistrationOpen && (
                          <div
                            className="row w-100 my-3"
                            style={{ zIndex: "9999" }}
                          >
                            {userLoading && (
                              <div className="col-12 text-center my-2">
                                <div
                                  className="spinner-border spinner-border-sm text-secondary"
                                  role="status"
                                />
                                <span className="f-13 mx-2">
                                  در حال بررسی رده سنی...
                                </span>
                              </div>
                            )}

                            {ageCheckDone && !userLoading && !genderEligible && (
                              <div className="col-12">
                                <div
                                  className="alert alert-warning text-center f-14 my-3"
                                  role="alert"
                                >
                                  {GENDER_INELIGIBLE_MESSAGE}
                                </div>
                              </div>
                            )}

                            {ageCheckDone && !userLoading && !matchAgeId && genderEligible && (
                              <div className="col-12">
                                <div
                                  className="alert alert-warning text-center f-14 my-3"
                                  role="alert"
                                >
                                  {AGE_INELIGIBLE_MESSAGE}
                                </div>
                              </div>
                            )}

                            {ageCheckDone &&
                              !userLoading &&
                              matchAgeId &&
                              genderEligible &&
                              !reserveEligible && (
                              <div className="col-12">
                                <div
                                  className="alert alert-warning text-center f-14 my-3"
                                  role="alert"
                                >
                                  {RESERVE_INELIGIBLE_MESSAGE}
                                </div>
                              </div>
                            )}

                            {ageCheckDone &&
                              !userLoading &&
                              matchAgeId &&
                              genderEligible &&
                              registrationKind === 'league' &&
                              approvedClubs.length === 0 && (
                              <div className="col-12">
                                <div className="alert alert-warning text-center f-14 my-3" role="alert">
                                  برای ثبت‌نام در لیگ باید حداقل یک باشگاه تأییدشده داشته باشید.
                                  <Link to="/panel/clubs/my" className="d-block mt-2">باشگاه‌های من</Link>
                                </div>
                              </div>
                            )}

                            {ageCheckDone &&
                              !userLoading &&
                              matchAgeId &&
                              genderEligible &&
                              registrationKind === 'league' &&
                              approvedClubs.length > 1 && (
                              <div className="col-12 col-md-6 mx-auto my-3">
                                <label className="f-14">انتخاب باشگاه</label>
                                <select
                                  className="form-control"
                                  value={selectedClubId}
                                  onChange={(e) => setSelectedClubId(e.target.value)}
                                >
                                  <option value="">انتخاب باشگاه</option>
                                  {approvedClubs.map((club) => (
                                    <option key={club.id} value={club.id}>
                                      {club.title}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}

                            {ageCheckDone &&
                              !userLoading &&
                              matchAgeId &&
                              genderEligible &&
                              reserveEligible &&
                              (registrationKind !== 'league' || approvedClubs.length > 0) && (
                              <div className="col-12 col-sm-12">
                                <div className="d-flex flex-row flex-wrap justify-content-center my-5">
                                  <button
                                    type="button"
                                    onClick={() => registerToMatch(false)}
                                    className="btn btn-danger mx-2 my-2"
                                    disabled={formSubmitLoading || walletLoading}
                                  >
                                    {formSubmitLoading ? (
                                      <div
                                        className="spinner-border spinner-border-sm text-white mb-1"
                                        role="status"
                                      ></div>
                                    ) : (
                                      <>
                                        <i className="fad fa-credit-card text-dark-mode f-18 mx-2"></i>
                                        ثبت‌نام و پرداخت
                                      </>
                                    )}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => registerToMatch(true)}
                                    className="btn btn-primary mx-2 my-2"
                                    disabled={formSubmitLoading || walletLoading}
                                  >
                                    {walletLoading ? (
                                      <div
                                        className="spinner-border spinner-border-sm text-white mb-1"
                                        role="status"
                                      ></div>
                                    ) : (
                                      <>
                                        <i className="fad fa-wallet text-dark-mode f-18 mx-2"></i>
                                        پرداخت با کیف پول
                                      </>
                                    )}
                                  </button>
                                </div>
                              </div>
                            )}
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        <Link
                          to={`/login?redirect=/match/${slug}`}
                          className="btn btn-danger mx-auto my-3"
                        >
                          <i className="fad fa-sign-in text-dark-mode f-18 mx-2"></i>
                          وارد شوید
                        </Link>
                      </>
                    )}
                  </div>

                  <div className="d-flex flex-row justify-content-center  w-100 p-2"></div>
                </div>
              </div>
              <div className="overflow-hidden card-event-label">
                {moment()
                  .local("fa")
                  .isAfter(
                    moment(
                      eventContext.event?.end_register_date,
                      "YYYY-MM-DD HH:mm:ss"
                    )
                  ) ? (
                  <>
                    <span className="bg-success text-dark-mode f-12 font-weight-bold">
                      پایان ثبت نام
                    </span>
                  </>
                ) : (
                  <>
                    {eventContext.event.capacity !== null &&
                    eventContext.event.capacity <=
                      eventContext.event?.users?.length ? (
                      <span className="bg-danger text-dark-mode f-12 font-weight-bold">
                        ظرفیت تکمیل
                      </span>
                    ) : null}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Master>
  );
};

export default Event;
